// TODO find a way to select form elements without importing classes into javascript
import mainStyles from "./styles.css";
import criticalStyles from "./critical.css";
const styles = { ...mainStyles, ...criticalStyles };
import formStyles from "../form-components/form-styles/styles.css";
import DDO from "../ddo/browser";
import { setupPlayer } from "../media-player/browser";
import { setUpEnhancedSlider } from "../enhanced-ui-slider/browser";
import DOMPurify from "dompurify";
import {
  initComponent,
  createTransactionID,
  setTabIndex,
  inIframe,
  makeGuid
} from "../../utilities/common";
import hashFnv32a from "../hashfnv32a/browser.js";
import { scrollIntoViewIfNeeded } from "../scroll-logic/browser";
import formDataEntries from "../form-components/formEntries";
import { postAnalytics } from "../../utilities/analytics.js";
import {
  normalizeAndHashEmail,
  normalizeAndHashPhone
} from "../../utilities/email-phone-hash.js";

const cookieName = "_mkto_trk";
const DEFAULT_BUFFER_FIELD_NAME = "__hp_buffer";
const googleAdParams = ["gclid", "gbraid", "wbraid"];

const hasGoogleAdParameter = () => {
  const params = new URLSearchParams(window.location.search);
  return googleAdParams.some((param) => params.get(param)?.trim());
};

const setGoogleUserData = (form, hashedEmail, hashedPhone) => {
  const hasAdPartnerConsent = form?.querySelector(
    'input[type="hidden"][name="ad_partner_consent"][value="true"]'
  );
  const hasEmailField = form?.querySelector('input[name="email"]');
  const hasPhoneField = form?.querySelector('input[name="phone"]');

  if (
    typeof window.gtag !== "function" ||
    !hasAdPartnerConsent ||
    !hasGoogleAdParameter()
  )
    return;

  const userData = {};

  if (hashedEmail && hasEmailField) {
    userData.sha256_email_address = hashedEmail;
  }

  if (hashedPhone && hasPhoneField) {
    userData.sha256_phone_number = hashedPhone;
  }

  if (!Object.keys(userData).length) return;

  window.gtag("set", "user_data", userData);
};

/*
 * checkValues fires when trying to move to the next step.
 * It accepts a callback to be run after the inputs are validated.
 * Stepped Form finds all the inputs on that step, and asks for their validation.
 * Validations can be either true/false, or in the case of service call validation,
 * promises that have to resolve or reject.
 * If any input returns false or rejects a promise, the callback passed to
 * checkValues does not run.
 *
 * changeStep handles the background changes of going to a new step.
 * changeRegion handles the hiding and revealing of the steps.
 * updateVerificationStep handles updating the values on the verification page.
 *
 * The static methods resetStyles, modifyInput, checkValidationTarget, and
 * checkValidation control the validation styling of inputs.
 * */

export default class SteppedForm {
  constructor(element, offerTypeOverride = "") {
    this.element = element;
    this.form = element.querySelector("form");
    // Dynamically assigns all the properties from the config on the index side to this.whateverVariable variables for usage in the browser (rather than doing this.thing = config.thing over and over)
    Object.assign(this, JSON.parse(this.element.dataset.config));

    this.stepHeader = this.element.querySelector(`.${styles.stepHeader}`);
    this.stepCounter = this.element.querySelector(`.${styles.stepCounter}`);
    // stepCounter is tabbable to allow screen readers to view the step number
    if (this.stepCounter) {
      setTabIndex([this.stepCounter], 0);
    }
    this.currentStep = 1;

    this.regions = [
      ...this.element.querySelectorAll(
        `[${
          this.isExpandingDrawer
            ? "data-type='drawers-container'"
            : "data-form-region"
        }]`
      )
    ];
    if (this.isExpandingDrawer) {
      this.regionTriggers = this.regions.map((region) =>
        region.querySelector("[data-type='drawer-opener']")
      );
      this.regionDrawers = this.regions.map((region) =>
        region.querySelector("[data-type='drawer-body']")
      );
      this.currentDrawer = this.regionDrawers[0];
      this.regionRemoteTriggerIds = this.regionTriggers.map(
        (region) => region.dataset.remoteTriggerId
      );
    }

    this.currentRegion = this.regions[0];

    this.verificationRegions = [
      ...this.element.querySelectorAll(`[data-form-verification-region]`)
    ];
    this.reachedSteps = [];
    this.loggedSteps = []; // track what steps are logged so we don't log the same one multiple times if they visit it multiple times

    this.isPauseAndAsk = this.form?.querySelector(`[name=pauseAndAsk]`)?.value;
    this.offerType =
      offerTypeOverride || this.form?.querySelector(`[name=offerType]`)?.value;
    this.sourceId = this.form?.querySelector(`[name=sourceId]`)?.value;
    this.steppedFormHeading =
      this.element?.querySelector(`.${styles.formHeading}`)?.innerText ||
      `${this.offerType} Stepped Form`;
    this.started = false;
    this.formSubmitted = false;
    this.isSimpleSignUp =
      this.form?.querySelector(`[name=fType]`)?.value === "simple-sign-up";
    this.submissionRedirect = JSON.parse(
      this.element.getAttribute("submissionRedirect")
    );

    this.submitButton = this.element.querySelector(
      `[data-type="form-submit-button"]`
    );

    this.usePreFillData = this.form.hasAttribute("usePreFillData");
    const { sourceId, formType } = this.getCurrentFormAnalyticsMetadata();
    this.analyticsFormData = {
      name: this.steppedFormHeading,
      id: sourceId,
      formType
    };

    this.errorMessage = this.element.querySelector(`[data-form-error]`);
    this.errorHeading = this.errorMessage?.querySelector("h3[analytics-title]");
    this.errorButton = this.errorMessage?.querySelector(`[data-type="button"]`);
    this.originalErrorMessage = this.errorHeading.innerHTML;

    this.formSessionId = makeGuid();
    this.setupButtonListeners();
    this.changeStep(this.currentStep, false);
    this.setupGeneralInputValidation();
    this.ensureBufferField();
    this.initFormConfig();
    this.setupFieldValidationListener();

    let cookie = document.cookie
      .split(";")
      .filter((cookie) => cookie.includes(cookieName))[0];

    if (cookie) {
      cookie = cookie.trim();

      element.querySelector("[name=marketoToken]").value = cookie.substring(
        cookieName.length + 1
      );
    }
  }

  ensureBufferField(fieldName = DEFAULT_BUFFER_FIELD_NAME) {
    let wrapper = this.form.querySelector("[data-form-buffer]");

    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.className = styles.optField;
      wrapper.setAttribute("data-type", "text-field");
      wrapper.setAttribute("data-form-buffer", "true");
      wrapper.setAttribute("extra-css", "input-text label");
      wrapper.setAttribute("textfieldinitialized", "");
      wrapper.innerHTML = `
        <div class="input-text_inputWrapper form-styles_left">
          <input
            class="form-styles_inputField input-text_input"
            type="text"
            name="${fieldName}"
            autocomplete="off"
          />
        </div>
      `;

      const slot = this.form.querySelector("[data-field-slot]");
      if (slot) {
        slot.replaceWith(wrapper);
      } else {
        this.form.appendChild(wrapper);
      }
    }

    const input = wrapper.querySelector("input");
    if (input && input.getAttribute("name") !== fieldName) {
      input.setAttribute("name", fieldName);
    }
    return input;
  }

  initFormConfig() {
    fetch(`${window.PUBLIC_ENV.CONSOLIDATION_PREFIX}/api/form-token`)
      .then((res) => res.json())
      .then(({ token, field }) => {
        this.fCfg = token;
        this.ensureBufferField(field);
      })
      .catch(() => {
        this.fCfg = null;
        this.ensureBufferField();
      });
  }

  getCurrentFormAnalyticsMetadata() {
    const sourceId =
      this.form?.querySelector(`[name=sourceId]`)?.value ?? this.sourceId;
    const offerType =
      this.form?.querySelector(`[name=offerType]`)?.value ?? this.offerType;

    return {
      sourceId,
      formType: inIframe() ? offerType + "|iFrame" : offerType
    };
  }

  logFormAnalytics(eventType, eventData = {}) {
    const { sourceId, formType } = this.getCurrentFormAnalyticsMetadata();
    const payload = {
      eventType,
      formSessionId: this.formSessionId,
      formName: this.steppedFormHeading,
      formType,
      sourceId,
      clientTimestamp: Date.now(),
      formToken: this.fCfg || null,
      eventData
    };

    fetch(`${window.PUBLIC_ENV.CONSOLIDATION_PREFIX}/api/form-analytics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {
      // Silently swallow — analytics should never block the user
    });
  }

  logFormErrorAnalytics(errorMessage, invalidFields = [], extraData = {}) {
    const eventData = { ...extraData };

    if (Array.isArray(invalidFields) && invalidFields.length > 0) {
      eventData.invalidFields = invalidFields;
    }

    if (errorMessage) {
      eventData.errorMessage = errorMessage;
    }

    this.logFormAnalytics("form_error", eventData);
  }

  setupFieldValidationListener() {
    const typeMap = {
      email: "email_validation",
      phone: "phone_validation",
      address: "address_validation"
    };

    this.form?.addEventListener("field-validated", (e) => {
      const { fieldType, valid } = e.detail || {};
      const eventType = typeMap[fieldType];
      if (eventType) {
        this.logFormAnalytics(eventType, { valid });
      }
    });
  }

  isFinalStep = (step) => step === this.numSteps;

  setDrawerValidity(drawer, validity) {
    if (this.isExpandingDrawer && drawer) drawer.setValid(validity);
  }

  getGoogleTagEventData() {
    const checkedRadios = this.form.querySelectorAll(
      'input[type="radio"][data-google-tag-event]:checked'
    );

    const eventData = {};

    checkedRadios.forEach((radio) => {
      const eventType = radio.getAttribute("data-google-tag-event");
      const value = radio.value;

      // Add the event-value pair to the data object
      if (eventType && value) {
        eventData[eventType] = value;
      }
    });

    return eventData;
  }

  getCurrentAnalyticsFormData() {
    const { sourceId, formType } = this.getCurrentFormAnalyticsMetadata();

    return {
      ...this.analyticsFormData,
      id: sourceId,
      formType
    };
  }

  submitHandler(event) {
    event.preventDefault();

    // Prevent double submission
    if (this.formSubmitted) {
      return;
    }
    const validatedSteps = [
      ...this.regions,
      ...(this.isExpandingDrawer
        ? [this.form.querySelector("[data-form-region]")]
        : [])
    ].map((r, index) => {
      return new Promise((resolve, reject) => {
        this.checkValues(
          () => {
            this.setDrawerValidity(this.regionDrawers?.[index], true);
            resolve();
          },
          () => {
            this.setDrawerValidity(this.regionDrawers?.[index], false);
            // This index allows us to navigate back to the step that failed validation.
            reject(index);
          },
          r,
          this.isFinalStep(index + 1) // logAnalytics is only true if we're checking values on the last "region" (because the others have already been logged)
        );
      });
    });

    // Switched from Promise.all to Promise.allSettled because sometimes certain steps would "validate" faster than other ones and it take the user to the wrong step
    Promise.allSettled(validatedSteps)
      .then((results) => {
        const failedIndex = results.find(
          (result) => result.status === "rejected"
        )?.reason;
        const lastIndex = this.numSteps - 1;

        if (failedIndex >= 0) {
          this.navigate(null, failedIndex + 1, this.regions[failedIndex], true);
          if (this.isExpandingDrawer)
            this.regionDrawers[lastIndex].setValid(false);
        } else {
          // Only submit if not already submitted (double-check)
          if (!this.formSubmitted) {
            this.submitForm();
          }
        }
      })
      .catch((error) => {
        // Handle any unexpected errors during validation
        console.error("Unexpected error during form validation:", error);
      });
  }

  getEndpoint() {
    if (this.isPauseAndAsk) return "pauseandask";
    if (this.isSimpleSignUp) return "simple-subscription";
    return "form-submission";
  }

  submitData(transactionID, data) {
    const req = new XMLHttpRequest();
    const endpoint = this.getEndpoint();
    const requestHeader = this.isSimpleSignUp
      ? "application/json"
      : "application/x-www-form-urlencoded";
    // Open a request and change the endpoint based on if it's a simple-sign-up or another form
    req.open(
      "POST",
      `${window.PUBLIC_ENV.CONSOLIDATION_PREFIX}/api/${endpoint}`
    );
    req.setRequestHeader("Content-type", requestHeader);
    // A single submission can drive this handler through the DONE state more
    // than once: when Akamai answers with 428 it shows the "I'm not a robot"
    // interstitial and then replays the request through its own XHR shim, and
    // the replayed response is delivered as several readystatechange callbacks
    // that all report readyState 4. Reporting per callback is what let Adobe
    // count one referral three times under a single transaction ID, so settle
    // the request once and ignore everything that arrives after that.
    let settled = false;

    req.onreadystatechange = () => {
      if (settled || req.readyState !== 4) {
        return;
      }

      // 428 is Akamai asking for the interstitial rather than a result, so
      // leave the request unsettled — the replayed response is the one we
      // report on.
      if (req.status === 428) {
        return;
      }

      settled = true;

      const updatedAnalyticsFormData = this.getCurrentAnalyticsFormData();

      if (req.status >= 200 && req.status < 300) {
        SteppedForm.sendAnalytics(
          {
            ...updatedAnalyticsFormData,
            transactionID,
            ...this.getGoogleTagEventData()
          },
          "formSubmit"
        );
        this.logFormAnalytics("form_submit", {
          endpoint,
          status: req.status
        });
        SteppedForm.displaySubmissionState(this.element, true, transactionID);
      } else {
        // Non-2xx submit response: emit a dedicated submit_failure event (the
        // failure counterpart of form_submit) with the status and a short
        // response snippet. status === 0 means a network-level abort.
        const errorMessage = `Submit failed: HTTP ${req.status}`;
        const responseSnippet =
          typeof req.responseText === "string" && req.responseText.length > 0
            ? req.responseText.slice(0, 200)
            : null;
        SteppedForm.sendAnalytics(
          {
            ...updatedAnalyticsFormData,
            error: `Server error (HTTP ${req.status}): ${
              req.statusText || "Data was not received."
            }`
          },
          "formError"
        );
        this.logFormAnalytics("submit_failure", {
          status: req.status,
          statusText: req.statusText || null,
          endpoint,
          responseSnippet,
          errorMessage
        });
        SteppedForm.displaySubmissionState(this.element, false);
      }
    };
    req.send(data);
  }

  static getRejectedMessage(rejectedInput) {
    const selectGroup = rejectedInput.closest(
      '[data-type="select-group"], [data-type="select-box"]'
    );
    const messageStorage = selectGroup?.querySelector(".rejected-message");
    return messageStorage?.innerHTML;
  }

  checkForRejectedSelections() {
    // Find all checked radio buttons, checkboxes, and select boxes with reject="true"
    const rejectedInputs = this.form.querySelectorAll(
      'input[type="radio"]:checked[reject="true"], input[type="checkbox"]:checked[reject="true"]'
    );

    // Find select boxes where the selected option has reject="true"
    const rejectedSelects = Array.from(
      this.form.querySelectorAll("select")
    ).filter((select) => {
      const selectedOption = select.options[select.selectedIndex];
      return selectedOption && selectedOption.getAttribute("reject") === "true";
    });
    // Combine both arrays
    const rejectedSelections = [...rejectedInputs, ...rejectedSelects];

    if (rejectedSelections.length > 0) {
      const customMessage = SteppedForm.getRejectedMessage(
        rejectedSelections[0]
      );
      if (customMessage && this.errorMessage && this.errorHeading) {
        // Hide the original error heading
        this.errorHeading.style.display = "none";
        this.errorButton.style.display = "none"; // Hide the try again button

        // Create or update custom reject message div
        let customDiv = this.errorMessage.querySelector(
          ".customRejectMessageDiv"
        );
        if (!customDiv) {
          customDiv = document.createElement("div");
          customDiv.className = "customRejectMessageDiv";
          this.errorMessage.insertBefore(
            customDiv,
            this.errorMessage.firstChild
          );
        }
        customDiv.innerHTML = DOMPurify.sanitize(customMessage);
        customDiv.style.display = "block";
      }
      return true;
    }
    return false;
  }

  submitForm() {
    // Prevent double submission at the start of the method
    if (this.formSubmitted) {
      return;
    }
    this.formSubmitted = true;

    const updatedAnalyticsFormData = this.getCurrentAnalyticsFormData();

    //  Check for rejected selections before submitting
    if (this.checkForRejectedSelections()) {
      // Display failure state instead of submitting
      SteppedForm.sendAnalytics(
        {
          ...updatedAnalyticsFormData,
          error: "Form submission rejected due to user selection"
        },
        "formError"
      );
      this.logFormErrorAnalytics(
        "Form submission rejected due to user selection"
      );
      SteppedForm.displaySubmissionState(this.element, false); // false = failure state
      return; // Exit early, don't submit the form
    }

    // Execute request verification before submitting
    this.executeRequestVerification()
      .then(async (requestId) => {
        const endpoint = this.getEndpoint();
        const transactionID = createTransactionID();
        DDO(transactionID);
        let currentTime = transactionID.slice(0, 13);
        let hashed = hashFnv32a(currentTime, true, "WuN9P62Arf2E");
        const formData = formDataEntries(this.form);

        formData.push(["transactionId", transactionID]);
        formData.push(["formSessionId", this.formSessionId]);
        formData.push(["hashed", hashed]);
        formData.push([
          "adobeId",
          window._satellite?.getVar("user_experience_cloud_id") || ""
        ]);
        SteppedForm.getCampaignIds(formData);
        formData.push(["requestId", requestId]);
        if (this.fCfg) {
          formData.push(["formToken", this.fCfg]);
        }

        // Always include consent checkbox values, and support hidden consent fields.
        const consentFields = [
          {
            name: "consent_to_message",
            getValue: (input) => input.checked
          },
          {
            name: "ad_partner_consent",
            getValue: (input) =>
              input.type === "checkbox" ? input.checked : input.value === "true"
          }
        ];

        consentFields.forEach(({ name, getValue }) => {
          const input = this.form.querySelector(`input[name="${name}"]`);
          if (input) {
            // Remove any existing entry to avoid duplicates
            const existingIndex = formData.findIndex(([key]) => key === name);
            if (existingIndex !== -1) {
              formData.splice(existingIndex, 1);
            }
            formData.push([name, getValue(input)]);
          }
        });

        if (endpoint === "form-submission") {
          const email = formData.find(([key]) => key === "email")?.[1];
          const phone = formData.find(([key]) => key === "phone")?.[1];
          const phoneCountryCode = formData.find(
            ([key]) => key === "phoneCountryCode"
          )?.[1];
          const hashedEmail = await normalizeAndHashEmail(email);
          const hashedPhone = await normalizeAndHashPhone(
            phone,
            phoneCountryCode
          );

          if (hashedEmail) {
            formData.push(["hashedEmail", hashedEmail]);
          }

          if (hashedPhone) {
            formData.push(["hashedPhone", hashedPhone]);
          }

          setGoogleUserData(this.form, hashedEmail, hashedPhone);
        }

        let encodedData = Array.from(formData, (e) =>
          e.map(encodeURIComponent).join("=")
        ).join("&");

        let dataObj = formData.reduce((prev, curr) => {
          return { ...prev, [curr[0]]: curr[1] };
        }, {});

        // If Simple Sign up, add data and use different API endpoint
        if (this.isSimpleSignUp)
          dataObj = SteppedForm.simpleSignUpDataAlteration(dataObj);

        // Add dynamoGUID from URL if the form is intended for pauseandask
        if (this.isPauseAndAsk) {
          const params = new URLSearchParams(window.location.search);
          const dynamoGUID = params.get("id");
          if (dynamoGUID) {
            encodedData += `&dynamoGUID=${encodeURIComponent(dynamoGUID)}`;
          }
        }

        this.submitData(
          transactionID,
          this.isSimpleSignUp ? JSON.stringify(dataObj) : encodedData
        );

        if (this.submissionRedirect) {
          let { URL, newTab } = this.submissionRedirect;
          window.open(URL, newTab);
        }
      })
      .catch((error) => {
        console.error("Request verification failed:", error);
        SteppedForm.sendAnalytics(
          {
            ...updatedAnalyticsFormData,
            error: "Request verification failed"
          },
          "formError"
        );
        // errorMessage must stay a fixed category — dashboards bucket by it,
        // so raw exception text (unique minified fragments) goes in errorDetail
        const detail = error?.message
          ? String(error.message).slice(0, 150)
          : null;
        this.logFormErrorAnalytics("recaptcha_verification_failed", [], {
          endpoint: this.getEndpoint(),
          ...(detail ? { errorDetail: detail } : {})
        });
        SteppedForm.displaySubmissionState(this.element, false);
        this.formSubmitted = false; // Allow retry
      });
  }

  // Method to execute request verification
  executeRequestVerification() {
    return new Promise((resolve, reject) => {
      if (!this.form) {
        reject(new Error("Form not available"));
        return;
      }
      // Use page-level verification token if provided
      if (window.PUBLIC_ENV.RECAPTCHA_BYPASS_TOKEN) {
        resolve(window.PUBLIC_ENV.RECAPTCHA_BYPASS_TOKEN);
        return;
      }
      // Check if site key is available
      if (!window.PUBLIC_ENV.REQUEST_ID_SITE_KEY) {
        console.warn(
          "Request verification service not configured, skipping verification"
        );
        resolve(""); // Return empty token if site key not available
        return;
      }

      // Check if verification is loaded
      const verificationService = window.grecaptcha;
      if (typeof verificationService === "undefined") {
        console.warn("Request verification not loaded, skipping verification");
        resolve("");
        return;
      }

      // For invisible verification
      verificationService.ready(() => {
        verificationService
          .execute(window.PUBLIC_ENV.REQUEST_ID_SITE_KEY, {
            action: "form_submit"
          })
          .then((token) => {
            resolve(token);
          })
          .catch((error) => {
            reject(error);
          });
      });
    });
  }

  changeStep(step, focus = false, remoteOpening = false) {
    if (this.showVerification) this.updateVerificationStep(this.currentStep);

    // If the current step is somehow below or above the current number of steps, we reset it
    const stepCap = step > this.numSteps ? this.numSteps : step;
    this.currentStep = step < 1 ? 1 : stepCap;

    if (this.isExpandingDrawer) {
      this.changeDrawer(this.currentStep, remoteOpening);
      return;
    }

    // i.e. Step 1 of 2
    this.stepCounter &&
      (this.stepCounter.innerHTML = `${
        this.stepWord ? `${this.stepWord} ` : ""
      }${this.currentStep} / ${this.numSteps}`);

    const hideInitially = (element) => {
      element?.[this.currentStep === 1 ? "setAttribute" : "removeAttribute"](
        "data-hidden",
        ""
      );
    };

    // The backButton should be hidden on the first step
    hideInitially(this.backButton);

    // The stepHeader should be hidden on the first step if hideStepsInitially is true
    if (this.hideStepsInitially) {
      hideInitially(this.stepHeader);
    }

    this.changeRegion(this.currentStep, focus);
  }

  changeRegion(step, focus) {
    this.currentRegion.hidden = true;
    this.currentRegion = this.regions[step - 1];
    this.currentRegion.hidden = false;
    // Only focus the selectBox if the user is keyboarding through the form
    if (focus) this.stepCounter?.focus();
  }

  changeDrawer(step, remoteOpening) {
    this.currentRegion = this.regions[step - 1];
    const newDrawer = this.regionDrawers?.[this.currentStep - 1];
    // If they're not on the drawer or something doesn't trigger it, toggle it
    if (this.currentDrawer !== newDrawer || !remoteOpening) {
      this.currentDrawer = newDrawer;
      this.currentDrawer.toggle();
    }

    // If the user skips a step, validate all previous steps
    this.regionDrawers.forEach((drawer, index) => {
      if (index >= step - 1) return;
      this.checkValues(
        () => {
          this.setDrawerValidity(drawer, true);
        },
        () => {
          this.setDrawerValidity(drawer, false);
        },
        this.regions[index]
      );
      this.updateVerificationStep(index + 1);
    });

    // If it's the final step, validate it as well
    if (this.isFinalStep(step)) {
      this.currentDrawer.setValid(this.finalDrawerValidation());
    }
    this.currentDrawer.focus();
  }

  trackStep(logAnalytics) {
    // Only log the completion if they haven't completed it previously
    if (!this.loggedSteps.includes(this.currentStep) && logAnalytics) {
      const steppedFormStepCompletedText = this.titles[this.currentStep - 1];
      const steppedFormStepCompletedNumber = this.currentStep;
      const steppedFormTotalSteps = this.numSteps;
      this.loggedSteps.push(this.currentStep);

      SteppedForm.sendAnalytics(
        {
          ...this.getCurrentAnalyticsFormData(),
          stepName: steppedFormStepCompletedText,
          stepNumber: `${steppedFormStepCompletedNumber}/${steppedFormTotalSteps}`
        },
        "formStepComplete"
      );

      this.logFormAnalytics("form_step_complete", {
        stepName: steppedFormStepCompletedText,
        stepNumber: steppedFormStepCompletedNumber,
        totalSteps: steppedFormTotalSteps
      });
    }
  }

  // new checkValues function that waits for address validation to complete before proceeding
  checkValues(resolve, reject, region, logAnalytics = true) {
    const validationRegion = region || this.currentRegion;

    // Get all form inputs and custom validation elements
    const allInputs = [
      ...validationRegion.querySelectorAll(
        `.${formStyles.inputField}, [data-type="select-group"]`
      )
    ];

    // Finds all custom form components that have their own checkIsValid() method (like email-field, phone-field, address-block)
    const customValidationElements = [
      ...validationRegion.querySelectorAll(`.${formStyles.formInputs}`)
    ].filter((e) => e.checkIsValid && typeof e.checkIsValid === "function");

    // calls helper method to separate elements for two validation phases.
    const { phase1Elements, phase2Elements } = SteppedForm.categorizeElements(
      allInputs,
      customValidationElements
    );

    // Phase 1: Validate non-address fields and empty address fields first. If any errors found, reject and stop
    SteppedForm.validatePhase(phase1Elements)
      .then((phase1Results) => {
        if (this.handleValidationErrors(phase1Results, phase1Elements)) {
          reject();
          return;
        }

        // Phase 1 passed, now validate Phase 2 (address fields with content)
        SteppedForm.validatePhase(phase2Elements)
          .then((phase2Results) => {
            // After address validation (which may include modal interaction),
            // we need to re-validate Phase 1 to ensure no other fields became invalid during modal interaction
            setTimeout(() => {
              if (phase2Elements.length > 0) {
                // If there were address elements, re-check Phase 1 to catch any changes made during modal
                SteppedForm.validatePhase(phase1Elements)
                  .then((recheckResults) => {
                    if (
                      this.handleValidationErrors(
                        recheckResults,
                        phase1Elements
                      )
                    ) {
                      reject(); // Phase 1 failed on recheck
                      return;
                    }

                    // Phase 1 still valid, now check Phase 2 results
                    if (
                      this.handleValidationErrors(phase2Results, phase2Elements)
                    ) {
                      reject();
                    } else {
                      this.trackStep(logAnalytics);
                      resolve();
                    }
                  })
                  .catch((recheckError) => {
                    if (recheckError && recheckError.stay === true) {
                      reject(recheckError);
                      return;
                    }
                    reject();
                  });
              } else {
                // No address elements, just check Phase 2 results normally
                if (
                  this.handleValidationErrors(phase2Results, phase2Elements)
                ) {
                  reject();
                } else {
                  this.trackStep(logAnalytics);
                  resolve();
                }
              }
            }, 100); // Slight delay to ensure all validations are processed
          })
          .catch((error) => {
            // Handle special case where address modal edit button was clicked
            if (error && error.stay === true) {
              reject(error); // Pass through the { stay: true } rejection
              return;
            }
            // Handle other validation errors normally
            reject();
          });
      })
      .catch((error) => {
        // Handle { stay: true } errors from Phase 1 as well
        if (error && error.stay === true) {
          reject(error);
          return;
        }
        // Handle other Phase 1 errors normally
        reject();
      });
  }

  // Helper method to categorize elements into validation phases
  static categorizeElements(allInputs, customValidationElements) {
    const phase1Elements = [];
    const phase2Elements = [];

    // Check if element is address-related
    const isAddressField = (element) => {
      if (element.name) {
        const lowerName = element.name.toLowerCase();
        if (lowerName.includes("addr") || lowerName.includes("address")) {
          return true;
        }
      }
      return (
        element.getAttribute("data-type") === "address-block" ||
        element.closest('[data-type="address-block"]')
      );
    };

    // Check if address field has content
    const hasAddressContent = (element) => {
      if (element.value?.trim()) return true;

      const addressInputs = element.querySelectorAll(
        'input[name*="addr"], input[name*="address"]'
      );
      return Array.from(addressInputs).some((input) => input.value?.trim());
    };

    // Phase 2: Address fields WITH content (may trigger modals)
    // Phase 1: Everything else (non-address fields + empty address fields)
    const addToPhase = (element) => {
      if (isAddressField(element) && hasAddressContent(element)) {
        if (!phase2Elements.includes(element)) {
          phase2Elements.push(element);
        }
      } else {
        if (!phase1Elements.includes(element)) {
          phase1Elements.push(element);
        }
      }
    };

    [...allInputs, ...customValidationElements].forEach(addToPhase);
    return { phase1Elements, phase2Elements };
  }

  // Helper method to validate a phase of elements
  static async validatePhase(elements) {
    const elementsWithValidation = elements.filter(
      (element) =>
        element.checkIsValid && typeof element.checkIsValid === "function"
    );
    const validationPromises = elementsWithValidation.map(async (element) => {
      try {
        const result = element.checkIsValid();
        if (result && typeof result.then === "function") {
          await result;
          return { element, valid: true };
        } else {
          return { element, valid: !!result };
        }
      } catch (error) {
        // Re-throw stay errors (edit button clicked), handle others as validation failures
        if (error && error.stay === true) {
          throw error;
        }
        return { element, valid: false, error };
      }
    });

    const results = await Promise.allSettled(validationPromises);

    // Check if any promises were rejected with { stay: true }
    const stayRejection = results.find(
      (result) =>
        result.status === "rejected" &&
        result.reason &&
        result.reason.stay === true
    );

    if (stayRejection) {
      throw stayRejection.reason;
    }

    // Apply validation styling to actual form inputs only
    elements.forEach((element) => {
      if (
        element.tagName === "INPUT" ||
        element.tagName === "SELECT" ||
        element.tagName === "TEXTAREA"
      ) {
        SteppedForm.checkValidation(element);
      }
    });

    return results;
  }

  // Helper method to handle validation errors and analytics
  handleValidationErrors(validationResults, elements) {
    // Track elements that passed their checkIsValid() methods
    const validatedElements = new Set();
    validationResults.forEach((result) => {
      if (
        result.status === "fulfilled" &&
        result.value &&
        result.value.valid &&
        result.value.element
      ) {
        validatedElements.add(result.value.element);
      }
    });

    // Find the first invalid element
    const firstInvalidElement = elements.find((element) => {
      if (validatedElements.has(element)) return false;

      if (element.tagName === "INPUT" || element.tagName === "SELECT") {
        return !element.checkValidity?.();
      }
      return element.getAttribute?.("data-validity") === "false";
    });

    // Get elements that failed their checkIsValid() methods
    const failedValidations = validationResults.filter(
      (result) =>
        result.status === "fulfilled" && result.value && !result.value.valid
    );

    if (firstInvalidElement || failedValidations.length > 0) {
      this.handleValidationFailure(
        elements,
        validatedElements,
        failedValidations,
        firstInvalidElement
      );
      return true; // Has errors
    }
    return false; // No errors
  }

  // Stable, locale-independent field key for analytics — never display text,
  // which fragments into a different value per locale downstream
  static getAnalyticsFieldKey(element) {
    const key = element?.name || element?.getAttribute?.("data-type") || null;
    // addr (address-block) and addr1 (address-field forms) are the same
    // logical field — report one canonical key
    return key === "addr1" ? "addr" : key;
  }

  // Helper method to handle validation failure (analytics and scrolling)
  handleValidationFailure(
    elements,
    validatedElements,
    failedValidations,
    firstInvalidElement
  ) {
    // Create list of invalid input names for analytics
    let invalidInputs = elements
      .filter(Boolean)
      .filter((element) => {
        if (validatedElements.has(element)) return false;

        if (element.tagName === "INPUT" || element.tagName === "SELECT") {
          return !element.checkValidity?.();
        }
        return element.getAttribute?.("data-validity") === "false";
      })
      .map((element) => SteppedForm.getAnalyticsFieldKey(element));

    if (invalidInputs.length === 0 && failedValidations.length > 0) {
      invalidInputs = failedValidations.map((f) =>
        SteppedForm.getAnalyticsFieldKey(f.value?.element)
      );
    }

    // One key per field; drop anything we couldn't resolve to a stable key
    invalidInputs = [...new Set(invalidInputs.filter(Boolean))];

    // Send analytics
    const errorAnalytics = {
      ...this.getCurrentAnalyticsFormData(),
      error: invalidInputs
    };
    SteppedForm.sendAnalytics(errorAnalytics, "formError");
    this.logFormErrorAnalytics(null, invalidInputs);

    // Scroll to first invalid field
    const firstInvalidField =
      firstInvalidElement || failedValidations[0]?.value?.element;
    if (firstInvalidField) {
      scrollIntoViewIfNeeded(
        firstInvalidField.previousElementSibling || firstInvalidField,
        {
          block: "center",
          behavior: "smooth"
        }
      );
    }
  }

  updateVerificationContent(values, step) {
    let hasErrors = false;
    const content = [
      ...new Set( // making it a set will remove duplicates
        values.map((v) => {
          if (v?.error) hasErrors = true;
          if (!Array.isArray(v.label)) v.label = [v.label];
          return v.label
            .map(
              (item) =>
                `<div${
                  v?.error ? ` class="${styles.missingValue}"` : ""
                }>${DOMPurify.sanitize(item, {
                  ALLOWED_TAGS: []
                })}</div>`
            )
            .join("");
        })
      )
    ].join("");

    if (content) {
      let template = document.createElement("template");
      const region = this.verificationRegions[step - 1];
      const values = region.querySelector(`.${styles.verificationValues}`);
      const headerAlert = region.querySelector(
        `.${styles.verificationRegionHeader} [data-icon-name='alert']`
      );

      // Show icon
      if (hasErrors && headerAlert?.hidden) headerAlert.hidden = false;
      // Hide icon
      else if (!hasErrors) headerAlert.hidden = true;

      template.innerHTML = `<div>${content.trim()}</div>`;
      while (values.firstChild) {
        values.removeChild(values.firstChild);
      }
      values.appendChild(template.content.firstChild);
    }
  }

  updateVerificationStep(step) {
    const values = !this.isFinalStep(step) // It's not the final step
      ? [
          ...this.regions[step - 1].querySelectorAll(
            `.${formStyles.formInputs} input, .${formStyles.formInputs} select, [data-type='select-group']`
          )
        ]
          .map((input) => SteppedForm.createReport(input))
          .filter((value) => value?.label)
      : [];

    // The expanding drawer form only updates the verification step once that drawer is opened, and thus the function ends early for that form type
    if (!values?.length && !this.isExpandingDrawer) return;
    this.updateVerificationContent(values, step);
  }

  navigate = (event, stepIndex, elemToScrollTo, remoteOpening) => {
    this.changeStep(stepIndex, event?.detail === 0, remoteOpening);
    scrollIntoViewIfNeeded(elemToScrollTo, { behavior: "smooth" });
  };

  finalDrawerValidation() {
    // If if the verificationStep is showing, it's the right step, and there's errors OR every value isn't filled - it's invalid
    return (
      !this.showVerification ||
      !this.isFinalStep(this.currentStep) ||
      ([
        ...this.currentRegion.querySelectorAll(`.${styles.verificationValues}`)
      ].every((node) => node.childNodes.length) &&
        !this.currentRegion.querySelector(`.${styles.missingValue}`))
    );
  }

  setupExpandingDrawerListeners(regionTriggers) {
    regionTriggers?.forEach((trigger, index) => {
      trigger?.addEventListener("click", (event) => {
        event.preventDefault();
        this.checkValues(
          () => {
            this.setDrawerValidity(
              this.currentDrawer,
              this.finalDrawerValidation()
            );
            this.navigate(event, index + 1, trigger);
          },
          (error) => {
            this.setDrawerValidity(this.currentDrawer, false);
            if (error?.stay !== true)
              // in certain cases (modal/slower verification) we reject with the value "stay" set to true to stop navigation from occurring too soon
              this.navigate(event, index + 1, trigger);
          },
          this.currentRegion
        );
      });
    });
  }

  nextButtonListener(event) {
    event.preventDefault();
    this.checkValues(
      () => {
        this.setDrawerValidity(
          this.currentDrawer,
          this.finalDrawerValidation()
        );
        this.navigate(
          event,
          this.currentStep + 1,
          this.currentDrawer || this.stepCounter
        );
      },
      () => {
        this.setDrawerValidity(this.currentDrawer, false);
      },
      this.currentRegion
    );
  }

  editButtonListener(event, button) {
    if (this.isExpandingDrawer) {
      this.currentDrawer.setValid(this.finalDrawerValidation());
    }
    const step = parseInt(button.dataset.region);
    this.navigate(event, step, this.regions[step - 1]);
  }

  setupButtonListeners() {
    this.backButton = this.element.querySelector(`.${styles.backButton}`);
    this.nextButtons = [
      ...this.element.querySelectorAll(`.${styles.nextButton}`)
    ];

    this.submitButton = this.element.querySelector('[type="submit"]');
    this.editButtons = this.element.querySelectorAll(
      `.${styles.verificationEditButton}`
    );

    this.backButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.navigate(event, this.currentStep - 1, this.stepCounter);
    });

    this.nextButtons?.forEach((b) => {
      b?.addEventListener("click", (event) => this.nextButtonListener(event));
    });

    if (this.isExpandingDrawer)
      this.setupExpandingDrawerListeners(this.regionTriggers);

    this.form?.addEventListener(
      "keydown",
      (event) => {
        const isPinDropSearchField =
          event.target?.matches?.("[data-location-field]") &&
          event.target?.closest?.('[data-type="pin-drop-modal"]');
        const isModalButton = event.target?.closest?.("button");

        if (
          event.key !== "Enter" ||
          isPinDropSearchField ||
          isModalButton ||
          event.target?.matches?.("[data-pin-drop-address-display]") ||
          !SteppedForm.isEventInsideOwnedModal(event.target, this.element) ||
          event.target instanceof HTMLTextAreaElement
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      },
      true
    );

    this.form?.addEventListener("submit", (event) => {
      if (
        !this.hasOpenModal() &&
        !SteppedForm.isEventInsideOwnedModal(event.target, this.element)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    });

    this.submitButton?.addEventListener("click", (event) => {
      event.preventDefault();

      if (
        this.hasOpenModal() ||
        SteppedForm.isFocusInsideOwnedModal(this.element)
      ) {
        return;
      }

      // Prevent multiple rapid clicks
      if (this.formSubmitted) {
        return;
      }
      this.submitHandler(event);
    });

    this.editButtons?.forEach((b) => {
      b.addEventListener("click", (event) => this.editButtonListener(event, b));
    });

    this.errorButton?.addEventListener("click", () => {
      this.hideErrorState(this.element);
    });
  }

  hasOpenModal() {
    return [...this.element.querySelectorAll('[data-type="modal"]')].some(
      (modal) => modal.getAttribute("aria-hidden") !== "true"
    );
  }

  static isFocusInsideOwnedModal(owner, target = document.activeElement) {
    return SteppedForm.isEventInsideOwnedModal(target, owner);
  }

  static isEventInsideOwnedModal(target, owner) {
    const modal = target?.closest?.('[data-type="modal"]');

    return Boolean(modal && owner?.contains(modal));
  }

  setupGeneralInputValidation() {
    [
      ...this.form.querySelectorAll(`.${formStyles.inputField}, [form-element]`)
    ].forEach((e) => {
      e.addEventListener("input", (e) => {
        if (!this.started) {
          this.logFormAnalytics("form_start");
        }
        SteppedForm.modifyInput(
          e,
          this.started,
          this.getCurrentAnalyticsFormData()
        );
        this.started = true;
      });
      e.addEventListener("focus", SteppedForm.resetStyles);

      if (this.usePreFillData) {
        e.value = sessionStorage.getItem(`saved-${e.name}`) || e.value;
      }
    });
  }

  hideErrorState(container) {
    // Restore original error heading
    if (this.errorHeading && this.originalErrorMessage) {
      this.errorHeading.innerHTML = DOMPurify.sanitize(
        this.originalErrorMessage
      );
      this.errorHeading.style.display = "block";
    }

    // Hide custom reject message div
    if (this.errorMessage) {
      const customDiv = this.errorMessage.querySelector(
        ".customRejectMessageDiv"
      );
      if (customDiv) {
        customDiv.style.display = "none";
      }
    }

    container.classList.remove(styles.formError);
    container.querySelector("[data-form-body]").hidden = false;
    container.querySelector("[data-form-error]").hidden = true;
    this.formSubmitted = false; // This enables the user to submit the form again, after "trying again"
    scrollIntoViewIfNeeded(container, { behavior: "smooth" });

    // If the form is Simple Sign Up, removed attribute for styling purposes
    if (this.isSimpleSignUp) container.removeAttribute("formSubmitted");
  }

  static resetStyles(event) {
    event.target.classList.remove(formStyles.valid, formStyles.invalid);
    this.form.dataset.displayHiddenFields = "true"; // Show hidden fields when user enters
  }

  static getBoncomCid = () => {
    return document.cookie
      ?.split("; ")
      ?.find((str) => str.startsWith("CID="))
      ?.split("=")[1]; // Borrowed from MDN: https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie#example_2_get_a_sample_cookie_named_test2
  };

  static modifyInput(event, formStarted, analyticsFormData) {
    let target = event.target;
    if (
      !target.dataset.changed &&
      !target.dataset.userModified &&
      !formStarted
    ) {
      // Sends form start data to the Data Layer for Adobe Launch
      SteppedForm.sendAnalytics(analyticsFormData, "formStart");
    }
    target.dataset.changed = "true";
    target.dataset.userModified = "true";
  }

  static checkValidationTarget(event) {
    let target = event.target;
    target.value = target.value.trim();
    SteppedForm.checkValidation(target);
  }

  static sendAnalytics(formData, event) {
    // Sends form data to the Data Layer for Adobe Launch
    postAnalytics({
      event: window.digitalDataEventsCUC.component[event],
      component: {
        info: formData,
        category: {
          primary: "form"
        }
      }
    });
  }

  static checkValidation(target) {
    if (!target) return;

    const isValid = target.checkValidity?.();

    if (isValid) {
      this.setAsValid(target);
    } else {
      this.setAsInvalid(target);
    }
  }

  static setAsValid(element) {
    element.classList.add(formStyles.valid);
    element.classList.remove(formStyles.invalid);
    element.dataset.validity = "true";
  }

  static setAsInvalid(element) {
    element.classList.add(formStyles.invalid);
    element.classList.remove(formStyles.valid);
    element.dataset.validity = "false";
  }

  static getPostSubmitCookieName(baseName, transactionId) {
    return transactionId ? `${baseName}_${transactionId}` : baseName;
  }

  static ensurePostSubmitTransactionField(postSubmitForm, transactionId) {
    if (!postSubmitForm || !transactionId) return;

    let transactionIdField = postSubmitForm.querySelector(
      'input[name="transactionId"]'
    );

    if (!transactionIdField) {
      transactionIdField = document.createElement("input");
      transactionIdField.type = "hidden";
      transactionIdField.name = "transactionId";
      postSubmitForm.appendChild(transactionIdField);
    }

    transactionIdField.value = transactionId;
  }

  static hydratePostSubmitForms(container, transactionId) {
    if (!container) return;

    const postSubmitForms = [
      ...container.querySelectorAll("[data-type='post-submit-data-gathering']")
    ];

    // If the browser never stored the post-submit readiness cookie, the
    // encrypted dynamoGuid cookie is not available either, so hide the
    // follow-up form rather than letting the user submit into a dead end.
    if (!SteppedForm.hasPostSubmitCookie(transactionId)) {
      postSubmitForms.forEach((element) => {
        element.hidden = true;
      });
      return;
    }

    postSubmitForms.forEach((element) => {
      const postSubmitForm = element.querySelector("form");
      SteppedForm.ensurePostSubmitTransactionField(
        postSubmitForm,
        transactionId
      );
    });
  }

  static hasPostSubmitCookie(transactionId) {
    const cookieName = SteppedForm.getPostSubmitCookieName(
      "postsubmit_ready",
      transactionId
    );
    return document.cookie
      ?.split("; ")
      ?.some((cookie) => cookie.startsWith(`${cookieName}=`));
  }

  static displaySubmissionState(
    container,
    successfulSubmission,
    transactionId
  ) {
    container.querySelector("[data-form-body]").hidden = true;
    const queryClass = successfulSubmission
      ? `[data-form-confirmation]`
      : `[data-form-error]`;
    const formSubmittedMessage = container.querySelector(queryClass);

    if (formSubmittedMessage) formSubmittedMessage.hidden = false;

    if (successfulSubmission) {
      SteppedForm.hydratePostSubmitForms(formSubmittedMessage, transactionId);
      [
        ...formSubmittedMessage.querySelectorAll(
          "[data-type='ui-slider-wrapper']"
        )
      ].forEach((slider) => {
        setUpEnhancedSlider(slider);
        [...slider.querySelectorAll('[data-type="media-player"]')].forEach(
          setupPlayer
        );
      });
      // For any encoded components (only simple sign up at this point), decode them
      [...formSubmittedMessage.querySelectorAll("[encoded-component]")]
        .filter((element) => element.innerHTML) // Only decode it if there IS innerHTML
        .forEach((el) => {
          el.innerHTML = decodeURI(el?.innerHTML).trim();
          setTimeout(() => {
            // Added a slight delay to account for everything rendering, then re-init the form so that the new form gets all the appropriate browser.js code
            init();
          });
        });
    } else {
      container.classList.add(styles.formError);
    }

    // If the form is a Simple Sign Up, add an attribute to the Simple Sign Up for styling purposes after the form has been submitted and processed
    if (this.isSimpleSignUp) container.setAttribute("formSubmitted", "");

    setTimeout(() => {
      // This wasn't working for the simple-sign-up, but if we add a small delay it works fine. Ideally look into a more dynamic fix in the future
      scrollIntoViewIfNeeded(formSubmittedMessage, {
        behavior: "smooth",
        block: "center"
      });
    }, 10);
  }

  static createReport = (input) => {
    if (input.dataset.isRequired === "true" || input.required) {
      const isSelectGroup = input.dataset.type === "select-group"; // Grab different label values depending on if it's a select-group or normal input
      let label = "";
      if (input.checkValidity?.()) {
        const nearestFormInput = input.closest(
          `.${formStyles.formInputs}:not([hide-verification-display])`
        );
        label = isSelectGroup
          ? input?.getValues?.()
          : nearestFormInput?.getValues?.();
        return { label };
      } else {
        label = `[${
          isSelectGroup
            ? input.querySelector("[analytics-title]")?.innerText
            : input?.parentElement?.querySelector("label")?.innerText
        }]`;
      }
      return { label, error: true };
    } else return {};
  };

  static getCampaignIds(formData) {
    const mdCampaign = document.getElementById("mdCampaign");
    formData.push(["MDCampaignId", mdCampaign?.dataset?.mdcampaignid || 10]);
    formData.push(["boncomCampaignId", SteppedForm.getBoncomCid() || 0]);
  }

  static simpleSignUpDataAlteration(dataObj) {
    const phoneNumber =
      dataObj.phone && dataObj.phoneCountryCode
        ? decodeURIComponent(dataObj.phoneCountryCode) + " " + dataObj.phone
        : null;
    const phone = phoneNumber
      ? {
          phoneNumber,
          whatsAppable: dataObj?.phoneFieldType === "whatsapp"
        }
      : undefined;
    const email = dataObj.email
      ? {
          emailAddress: decodeURIComponent(dataObj.email)
        }
      : undefined;
    const channel = (phoneNumber && dataObj?.phoneFieldType) || "email";

    return {
      person: { firstName: dataObj.fullName },
      phone,
      email,
      countryIso2: (phoneNumber && dataObj.phoneCountry) || dataObj.countryIso2,
      languageIso3: dataObj.lang,
      requestId: dataObj.requestId,
      ...(dataObj?.missionaries &&
        dataObj?.address &&
        dataObj?.timeSelection && {
          messagingMergeCodes: {
            missionaries: dataObj?.missionaries || "",
            address: dataObj?.address || "",
            timeSelection: dataObj?.timeSelection || ""
          }
        }),
      subscriptions: [
        {
          channel,
          cid: SteppedForm.getBoncomCid() || 0,
          clientRegion: dataObj.clientRegion,
          listId: dataObj.sourceId,
          siteUrl: window.location.href,
          subscribed: true
        }
      ]
    };
  }
}

export const init = () => {
  initComponent("stepped-form", (element) => new SteppedForm(element));
};
init();
